// src/components/PeoplePlan/TheBench/hooks/useWorkflowFilters.js
import { useState, useEffect } from 'react';

const useWorkflowFilters = (employees) => {
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  
  // Status filters (default to New and In Progress)
  const [selectedStatuses, setSelectedStatuses] = useState(["New", "In Progress"]);
  
  // Column filters for table view
  const [columnFilters, setColumnFilters] = useState({
    employee_name: [],
    mum_name: [],
    corp: [],
  });

  // Kanban filters
  const [kanbanFilters, setKanbanFilters] = useState({
    employee_name: "all",
    mum_name: "all",
    workflow_name: "all",
    corp: "all",
  });

  // Initialize column filters with all values when employees change
  useEffect(() => {
    if (employees.length > 0) {
      const uniqueEmployeeNames = [...new Set(employees.map((e) => e.employee_name || "N/A"))];
      const uniqueMumNames = [...new Set(employees.map((e) => e.mum_name || "N/A"))];
      const uniqueLocations = [...new Set(employees.map((e) => e.corp || "N/A"))];

      console.log("Setting column filters:", {
        employee_name: uniqueEmployeeNames.length,
        mum_name: uniqueMumNames.length,
        corp: uniqueLocations.length,
      });

      setColumnFilters({
        employee_name: uniqueEmployeeNames,
        mum_name: uniqueMumNames,
        corp: uniqueLocations,
      });
    }
  }, [employees]);

  // Apply all filters
  useEffect(() => {
    let filtered = [...employees];

    // Apply status filter
    if (!selectedStatuses.includes("All statuses") && selectedStatuses.length > 0) {
      filtered = filtered.filter((emp) =>
        selectedStatuses.includes(emp.status)
      );
    }

    // Apply column filters (only for table view - check in parent)
    Object.entries(columnFilters).forEach(([column, selectedValues]) => {
      if (selectedValues.length > 0 && selectedValues.length < employees.length) {
        filtered = filtered.filter((emp) => {
          const value = emp[column] || "N/A";
          return selectedValues.includes(value);
        });
      }
    });

    // Apply Kanban filters
    Object.entries(kanbanFilters).forEach(([field, value]) => {
      if (value !== "all") {
        filtered = filtered.filter((emp) => {
          const empValue = emp[field] || "N/A";
          return empValue === value;
        });
      }
    });

    setFilteredEmployees(filtered);
  }, [selectedStatuses, columnFilters, employees, kanbanFilters]);

  // Status handling - now handles both checkbox mode and dropdown mode
  const handleStatusChange = (statusOrArray, isChecked = null) => {
    // If an array is passed (from dropdown in integrated mode)
    if (Array.isArray(statusOrArray)) {
      setSelectedStatuses(statusOrArray);
      return;
    }

    // Otherwise, handle as checkbox change (standalone mode)
    const status = statusOrArray;
    
    if (status === "All statuses") {
      if (isChecked) {
        setSelectedStatuses([
          "All statuses",
          "New",
          "In Progress",
          "Completed",
          "Rejected",
        ]);
      } else {
        setSelectedStatuses(["New", "In Progress"]);
      }
    } else {
      setSelectedStatuses((prev) => {
        let updated = prev.filter((s) => s !== "All statuses");
        if (isChecked) {
          updated = [...updated, status];
        } else {
          updated = updated.filter((s) => s !== status);
        }
        return updated;
      });
    }
  };

  // Handle column filter changes
  const handleColumnFilterChange = (column, selectedValues) => {
    setColumnFilters((prev) => ({
      ...prev,
      [column]: selectedValues,
    }));
  };

  // Handle Kanban filter changes
  const handleKanbanFilterChange = (field, value) => {
    console.log(`${field} filter changed to:`, value);
    setKanbanFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Reset Kanban filters
  const resetKanbanFilters = () => {
    console.log("Resetting all filters");
    setKanbanFilters({
      employee_name: "all",
      mum_name: "all",
      workflow_name: "all",
      corp: "all",
    });
  };

  // Get unique values for dropdowns
  const getUniqueValues = (field) => {
    console.log(`Getting unique values for ${field}`);
    
    if (!employees || employees.length === 0) {
      console.log(`No employees data available for ${field}`);
      return [];
    }

    const values = [...new Set(employees.map((emp) => emp[field] || "N/A"))];
    console.log(`Unique values for ${field}:`, values);
    return values.sort();
  };

  return {
    filteredEmployees,
    selectedStatuses,
    columnFilters,
    kanbanFilters,
    handleStatusChange,
    handleColumnFilterChange,
    handleKanbanFilterChange,
    resetKanbanFilters,
    getUniqueValues
  };
};

export default useWorkflowFilters;